// lib/cascade/phases.js
// ═══════════════════════════════════════════════════════════
// CASCADE INTEGRATION PLAN — phase status data
//
// Source: docs/CONTINUUM_CASCADE_INTEGRATION.md §VII (the 6-phase
// plan, extended with H-3v2 Phase A run + Phase 1.5 + Phase 5).
//
// Read-only v1: hardcoded status, source-of-truth is the doc.
// Future (Phase 6/7): migrate to DB-backed with editable status.
// ═══════════════════════════════════════════════════════════

export const PHASES = [
  {
    id: 0,
    label: 'Phase 0',
    title: 'Substrate-symmetric framework',
    status: 'complete',
    completedDate: '2026-05-04',
    summary: '17-dimension framework + three asymmetries + six eco-teaches-human reframes. Methodology only, no code.',
    artifact: 'docs/CONTINUUM_CASCADE_INTEGRATION.md',
    killSwitchVerdict: 'Symmetric mapping holds for 14+ of 17 dimensions; the three asymmetries are findings, not failures.',
  },
  {
    id: 1,
    label: 'Phase 1',
    title: 'Pre-register cascade hypotheses',
    status: 'complete',
    completedDate: '2026-05-04',
    summary: 'H-Continuum-9 through H-15 registered with falsifiers, statistical tests, paired case sets.',
    artifact: 'docs/CONTINUUM_HYPOTHESIS_FORMAT.md §V-bis',
    killSwitchVerdict: '7/7 hypotheses survived the §III discipline validity check (≥5 required).',
  },
  {
    id: 1.5,
    label: 'Phase 1.5',
    title: 'Cross-substrate transfer hypotheses',
    status: 'complete',
    completedDate: '2026-05-05',
    summary: 'H-16 (refugia), H-17 (Allee mechanism — H-15 sub-test), H-18 (hysteresis mechanism — H-15 sub-test) registered.',
    artifact: 'docs/CONTINUUM_HYPOTHESIS_FORMAT.md §V-bis',
    killSwitchVerdict: 'All 3 transfers passed §III discipline rules; H-15 keystone now has two operationalizable sub-tests.',
  },
  {
    id: 2,
    label: 'Phase 2',
    title: 'Korea cascade + Atlantic cod analog',
    status: 'first-pass-complete',
    completedDate: '2026-05-05',
    summary: 'Both cases populated with 17-dim framework + intervention grids. Striking finding: same arrest-failure architecture (Policy-heavy, Cultural-absent, both stabilization-at-lower).',
    artifact: 'docs/CONTINUUM_PHASE_2_KOREA_COD.md',
    killSwitchVerdict: 'Cascade analysis produces net-new moves on Korea beyond Phase A; cod analog yields novel parallel.',
  },
  {
    id: 3,
    label: 'Phase 3',
    title: 'Cross-substrate transfer queries',
    status: 'first-pass-complete',
    completedDate: '2026-05-05',
    summary: 'Seven transfers documented; top three (refugia, Allee, hysteresis) registered as H-16/-17/-18.',
    artifact: 'docs/CONTINUUM_PHASE_3_TRANSFERS.md',
    killSwitchVerdict: 'PASSED — ≥3 falsifiable transfers produce novel signal (≥1 required).',
  },
  {
    id: 4,
    label: 'Phase 4',
    title: 'Iran-Korea MDSD with eco cross-check',
    status: 'first-pass-complete',
    completedDate: '2026-05-05',
    summary: 'Iran 17-dim framework + Iran-Korea cross-comparison. Eco cross-check moved verdicts on 3 of 4 findings. Iran post-2014 is new H-18 anchor case.',
    artifact: 'docs/CONTINUUM_PHASE_4_IRAN_KOREA.md',
    killSwitchVerdict: 'PASSED — eco analysis is not decoration; substrate-symmetric thesis has live empirical leverage.',
  },
  {
    id: 'H-3v2',
    label: 'Phase A',
    title: 'H-Continuum-3v2 red-team run',
    status: 'complete',
    completedDate: '2026-05-05',
    summary: 'First Phase A run on a cascade-integration-era hypothesis. UN WPP gap of 0.54 at threshold-boundary. SURVIVED-RESPECIFIED → H-Continuum-3v3 proposed.',
    artifact: 'docs/CONTINUUM_H_3V2_PHASE_A_RUN.md',
    killSwitchVerdict: 'Methodology validated. Conjoint structural features (refugia + narrative + metapopulation) explain gap better than late-transition timing alone.',
  },
  {
    id: 5,
    label: 'Phase 5',
    title: 'Source stack audit',
    status: 'complete',
    completedDate: '2026-05-05',
    summary: 'Identified four gaps: Tier 4 cultural (highest), Tier 3 institutional, UN WPP formal pull, sub-national TFR variance. ~10-15 sessions of Phase 7 engineering before hypothesis set is empirically testable at scale.',
    artifact: 'docs/CONTINUUM_PHASE_5_SOURCE_AUDIT.md',
    killSwitchVerdict: 'Source-stack work is upstream of hypothesis testing for H-11, H-18, H-3v3.',
  },
  {
    id: 6,
    label: 'Phase 6',
    title: 'Schema decisions',
    status: 'pending',
    summary: 'Substrate-agnostic schema: substrate column, arrest_classification, metapopulation_structure, refugia_present, human_reflexive_layer_engaged. Decisions before any migrations.',
    artifact: null,
    killSwitchVerdict: null,
  },
  {
    id: 7,
    label: 'Phase 7',
    title: 'Engineering',
    status: 'pending',
    summary: 'Migrations + grid loader + three-query engine + cross-substrate transfer engine + admin Lab surface. ~10-15 sessions estimated.',
    artifact: null,
    killSwitchVerdict: null,
  },
];

// Status → color (Continuum tokens.js compatible)
export const STATUS_COLOR = {
  complete: 'sage',
  'first-pass-complete': 'gold',
  pending: 'dim',
  blocked: 'terra',
};

// Status → label
export const STATUS_LABEL = {
  complete: 'COMPLETE',
  'first-pass-complete': 'FIRST PASS',
  pending: 'PENDING',
  blocked: 'BLOCKED',
};
